import { useState, useEffect, useRef } from "react";
import {
  Button,
  TextInput,
  Switch,
  Group,
  Modal,
  Table,
  Text,
  Divider,
  Alert,
  Select,
  Grid,
  Textarea,
  Accordion,
  AccordionItem,
  NumberInput,
  Tooltip,
  Menu,
  ActionIcon,
  Box,
  ScrollArea,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { DragDropContext, Draggable, Droppable, DropResult } from "react-beautiful-dnd";
import { ArrowBackUp, DeviceFloppy, Edit, GripVertical, Plus, Trash, Robot, TestPipe, Bookmark, ChevronDown } from "tabler-icons-react";
import FieldEditor from "./field-editor";
import { getFieldLabelFromType } from "../../hooks/flexFormUtils";
import { useStoryTemplate } from "../../hooks/useStoryTemplate";
import { generateHandlebarsTemplates } from "../../components/shared/hooks/template-functions";
import { useTemplateTester } from "../../hooks/useTemplateTester";
import { FlexFormTemplate, FormFieldConfig, isValidE164PhoneNumber } from "@captiva/shared-types";
import { z } from "zod";

const FormTemplateValidationSchema = z.object({
  name: z.string().min(1, "Name is required"),
  fields: z.array(z.object({
    id: z.string().min(1, "Field ID is required"),
    key: z.string().min(1, "Field key is required"),
    label: z.string().min(1, "Field label is required"),
    type: z.string().min(1, "Field type is required"),
  })).min(1, "At least one field is required"),
});
import { TemplateReferenceMenu } from "../../components/shared";

interface FormTemplateEditorProps {
  template: Partial<FlexFormTemplate>;
  onSave: (template: Partial<FlexFormTemplate>) => Promise<Partial<FlexFormTemplate>>;
  onCancel: () => void;
  allTemplates: FlexFormTemplate[];
  companyId: string;
}

function flattenObject(obj: Record<string, any>, prefix = ""): Record<string, any> {
  const flat: Record<string, any> = {};
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      const value = obj[key];
      const newKey = prefix ? `${prefix}_${key}` : key;
      if (typeof value === "object" && value !== null && !Array.isArray(value)) {
        Object.assign(flat, flattenObject(value, newKey));
      } else {
        flat[newKey] = value || "";
      }
    }
  }
  return flat;
}

function getAlaSqlFlattenedData(data: Record<string, any> | undefined): Record<string, any> {
  if (!data) return {};
  return flattenObject(data);
}

export default function FormTemplateEditor({
  template,
  onSave,
  onCancel,
  allTemplates,
  companyId,
}: FormTemplateEditorProps) {
  const isMobile = useMediaQuery('(max-width: 768px)');
  const inputSize = isMobile ? "md" : "sm";
  const [fields, setFields] = useState<FormFieldConfig[]>(template.fields || []);
  const [name, setName] = useState(template.name || "");
  const [description, setDescription] = useState(template.description || "");
  const [active, setActive] = useState(template.active !== false);
  const [prefix, setPrefix] = useState(template.prefix || "S");
  const [hideNullishInPdf, setHideNullishInPdf] = useState(template.hideNullishInPdf || false);
  const [pdfLogoMaxWidth, setPdfLogoMaxWidth] = useState(template.pdfLogoMaxWidth ?? 60);
  const [pdfPhotoMaxWidth, setPdfPhotoMaxWidth] = useState(template.pdfPhotoMaxWidth ?? 80);
  const [pdfSignatureMaxWidth, setPdfSignatureMaxWidth] = useState(template.pdfSignatureMaxWidth ?? 120);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingField, setEditingField] = useState<FormFieldConfig | null>(null);
  const accordionContainerRef = useRef<HTMLDivElement>(null);

  const {
    storyTemplate,
    setStoryTemplate,
    error: storyError,
    saveStoryTemplate,
  } = useStoryTemplate(template.id || "", companyId);

  const {
    testModalOpen,
    setTestModalOpen,
    testResult,
    testing,
    testTemplates: runTestTemplates,
    rawSubmission,
    flattenedData,
  } = useTemplateTester(template.id || "", companyId);

  const [notificationEmails, setNotificationEmails] = useState<string[]>(template.notificationEmails || []);
  const [notificationWhatsappNumbers, setNotificationWhatsappNumbers] = useState<string[]>(template.notificationWhatsappNumbers || []);
  const [notificationSubjectLine, setNotificationSubjectLine] = useState(
    template.notificationSubjectLine || "New Form Submission: {{documentNumber}}"
  );
  const [notificationBody, setNotificationBody] = useState(
    template.notificationBody || "A new form has been submitted with document number {{documentNumber}}."
  );
  const [suggestionsModalOpen, setSuggestionsModalOpen] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState<{ subject: string; body: string; story: string } | null>(null);
  const [generating, setGenerating] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [dataModalOpen, setDataModalOpen] = useState(false);
  const isFromModule = !!template.moduleId;

  const [isPublicCaptureEnabled, setIsPublicCaptureEnabled] = useState(template.isPublicCaptureEnabled || false);
  const [requireAuthentication, setRequireAuthentication] = useState(template.requireAuthentication !== false);
  const [guestSubmitterLabel, setGuestSubmitterLabel] = useState(template.guestSubmitterLabel || "Web Guest");

  // Drag and drop handler
  const onDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    const reordered = Array.from(fields);
    const [removed] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, removed);
    setFields(reordered);
  };

  // Add/edit field modal
  const openFieldModal = (fieldObj?: FormFieldConfig) => {
    setEditingField(fieldObj || ({ id: "", label: "", type: "text" } as FormFieldConfig));
    setModalOpen(true);
  };

  const saveField = (fieldObj: FormFieldConfig) => {
    setFields((prev) => {
      const idx = prev.findIndex((f) => f.id === fieldObj.id);
      if (idx > -1) {
        const copy = [...prev];
        copy[idx] = fieldObj;
        return copy;
      }
      return [...prev, fieldObj];
    });
    setModalOpen(false);
  };

  const deleteField = (fieldId: string) => setFields(fields.filter((f) => f.id !== fieldId));

  const addEmail = () => setNotificationEmails([...notificationEmails, ""]);

  const updateEmail = (i: number, val: string) => {
    const copy = [...notificationEmails];
    copy[i] = val;
    setNotificationEmails(copy);
  };

  const removeEmail = (i: number) => setNotificationEmails(notificationEmails.filter((_, idx) => idx !== i));

  const addWhatsapp = () => setNotificationWhatsappNumbers([...notificationWhatsappNumbers, ""]);

  const updateWhatsapp = (i: number, value: string) => {
    const copy = [...notificationWhatsappNumbers];
    copy[i] = value;
    setNotificationWhatsappNumbers(copy);
  };

  const removeWhatsapp = (i: number) => setNotificationWhatsappNumbers(notificationWhatsappNumbers.filter((_, idx) => idx !== i));

  const insertPlaceholder = (type: "email" | "whatsapp", index: number, placeholder: string) => {
    if (type === "email") {
      const copy = [...notificationEmails];
      copy[index] = placeholder;
      setNotificationEmails(copy);
    } else {
      const copy = [...notificationWhatsappNumbers];
      copy[index] = placeholder;
      setNotificationWhatsappNumbers(copy);
    }
  };


  const generateTemplates = async () => {
    setGenerating(true);
    try {
      const data = await generateHandlebarsTemplates(fields);
      setAiSuggestions(data);
      setSuggestionsModalOpen(true);
    } catch (error) {
      console.error("Error generating templates:", error);
      setErrorMessage("An error occurred while generating templates. Please try again later.");
    } finally {
      setGenerating(false);
    }
  };

  const acceptSuggestions = () => {
    if (aiSuggestions) {
      setNotificationSubjectLine(aiSuggestions.subject);
      setNotificationBody(aiSuggestions.body);
      setStoryTemplate(aiSuggestions.story);
    }
    setSuggestionsModalOpen(false);
  };

  const handleSave = async () => {
    const isTemplate = (str: string) => str.includes('{{') && str.includes('}}');
    const validWhatsappNumbers = notificationWhatsappNumbers.filter(w => w.trim());
    for (const number of validWhatsappNumbers) {
      if (!isTemplate(number) && !isValidE164PhoneNumber(number)) {
        alert(`Invalid WhatsApp number: ${number}. Must be in E.164 format (e.g. +27821234567) or a template placeholder (e.g. {{customer.phone}})`);
        return;
      }
    }
    try {
      // Create data object
      const templateData = {
        ...template,
        name,
        description,
        active,
        fields: JSON.parse(JSON.stringify(fields)),
        notificationEmails: notificationEmails.filter(e => e.trim()),
        notificationWhatsappNumbers: validWhatsappNumbers,
        prefix,
        notificationSubjectLine,
        notificationBody,
        hideNullishInPdf,
        pdfLogoMaxWidth,
        pdfPhotoMaxWidth,
        pdfSignatureMaxWidth,
        isPublicCaptureEnabled,
        requireAuthentication,
        guestSubmitterLabel,
        archived: (template as any).archived ?? false,
      };

      const validationResult = FormTemplateValidationSchema.safeParse(templateData);
      if (!validationResult.success) {
        console.error("Form validation failed:", validationResult.error);
        alert(`Validation failed:\n${validationResult.error.issues.map((e: any) => `${e.path?.join('.')}: ${e.message}`).join('\n')}`);
        return;
      }

      // Save template first to get the ID
      const savedTemplate = await onSave(templateData);
      // Then save story template with the correct ID
      await saveStoryTemplate(storyTemplate, savedTemplate.id);
    } catch (error) {
      console.error("Error saving:", error);
    }
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: isMobile ? "100%" : "auto",
        maxHeight: isMobile ? "100%" : "75vh",
        overflow: "hidden",
      }}
    >
      <ScrollArea
        style={{ flex: isMobile ? 1 : "0 1 auto", minHeight: 0 }}
        styles={{
          viewport: {
            maxHeight: isMobile ? "100%" : "calc(75vh - 68px)",
            overflowX: "hidden",
          },
          scrollbar: {
            '&[data-orientation="horizontal"]': {
              display: "none",
            },
          },
        }}
        p="xs"
        pb={isMobile ? "xs" : "md"}
        offsetScrollbars={isMobile}
        type="auto"
      >
      {isFromModule && (
        <Alert color="orange" mb="sm" icon={<Edit size={16} />}>
          <Text weight={500}>Module Template</Text>
          <Text size="sm">
            This template was installed from a module. You can modify it, but changes may affect module functionality.
            Consider creating a custom template instead if you need significant modifications.
          </Text>
        </Alert>
      )}
      {errorMessage && (
        <Alert color="red" mb="sm" title="Error" onClose={() => setErrorMessage(null)}>
          {errorMessage}
        </Alert>
      )}
      {storyError && (
        <Alert color="red" mb="sm" title="Story Error">
          {storyError}
        </Alert>
      )}
      <Grid columns={isMobile ? 1 : 4} gutter="sm" mb="sm">
        <Grid.Col span={isMobile ? 1 : 3}>
          <TextInput
            size={inputSize}
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            labelProps={{ style: { fontSize: "16px" } }}
          />
        </Grid.Col>
        <Grid.Col span={1}>
          <Switch
            size={inputSize}
            color="cap-blue"
            label="Active"
            checked={active}
            onChange={(e) => setActive(e.currentTarget.checked)}
            mt={isMobile ? "xs" : "xl"}
          />
        </Grid.Col>
        <Grid.Col span={isMobile ? 1 : 3}>
          <TextInput
            size={inputSize}
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            labelProps={{ style: { fontSize: "16px" } }}
          />
        </Grid.Col>
        <Grid.Col span={1}>
          <Select
            size={inputSize}
            label="Doc number prefix"
            value={prefix}
            onChange={(val) => {
              if (val) setPrefix(val);
            }}
            data={["A", "B", "C", "D", "E", "F", "G", "H", "I", "J", "K", "L", "M", "N", "O", "P", "Q", "R", "S", "T", "U", "V", "W", "X", "Y", "Z"]}
            required
            searchable
            labelProps={{ style: { fontSize: "16px" } }}
          />
        </Grid.Col>
      </Grid>
      <div ref={accordionContainerRef}>
      <Accordion
        style={{ borderRadius: "8px", border: "1px solid #e0e0e0" }}
        iconPosition="right"
        onChange={(state) => {
          if (isMobile && state && accordionContainerRef.current) {
            const openedEntries = Object.entries(state).filter(([, opened]) => opened);
            if (openedEntries.length > 0) {
              const lastOpenedIndex = Number(openedEntries[openedEntries.length - 1][0]);
              setTimeout(() => {
                const items = accordionContainerRef.current?.querySelectorAll('.mantine-Accordion-item');
                if (items && items[lastOpenedIndex]) {
                  items[lastOpenedIndex].scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                }
              }, 150);
            }
          }
        }}
      >
        <AccordionItem label="Fields">
          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId="fields">
              {(provided) => (
                <div style={{ maxHeight: isMobile ? undefined : "400px", overflow: isMobile ? "visible" : "auto" }}>
                  <Table
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                    striped
                    highlightOnHover
                  >
                    <tbody>
                      {fields.map((field, idx) => (
                        <Draggable key={field.id} draggableId={field.id} index={idx}>
                          {(provided) => (
                            <tr ref={provided.innerRef} {...provided.draggableProps}>
                              <td {...provided.dragHandleProps} style={{ cursor: "grab", textAlign: "center", padding: "8px" }}>
                                <GripVertical size={18} />
                              </td>
                              <td>
                                {field.label}
                                {["gps", "barcode", "multiBarcode", "nfc"].includes(field.type) && isPublicCaptureEnabled && (
                                  <Tooltip
                                    label="This form allows online capture. User device and browser may limit capability."
                                    withArrow
                                    position="right"
                                  >
                                    <span style={{ color: '#e67e22', marginLeft: '6px', fontSize: '11px', fontWeight: 500, cursor: 'help' }}>
                                      ⚠️ Online
                                    </span>
                                  </Tooltip>
                                )}
                              </td>
                              <td>{getFieldLabelFromType(field.type)}</td>
                              <td>
                                <div style={{ display: "flex", justifyContent: "space-around", gap: 8 }}>
                                  <Button
                                    size={inputSize}
                                    color="cap-blue"
                                    variant="outline"
                                    onClick={() => openFieldModal(field)}
                                    style={{ minHeight: isMobile ? 44 : 36, minWidth: isMobile ? 44 : 36 }}
                                  >
                                    <Edit size={16} />
                                  </Button>
                                  <Button
                                    size={inputSize}
                                    color="red"
                                    variant="outline"
                                    onClick={() => deleteField(field.id)}
                                    style={{ minHeight: isMobile ? 44 : 36, minWidth: isMobile ? 44 : 36 }}
                                  >
                                    <Trash size={16} />
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </tbody>
                  </Table>
                </div>
              )}
            </Droppable>
          </DragDropContext>
          <Group position="apart" mb="sm">
            <Button size={inputSize} color="cap-blue" variant="outline" mt="sm" leftIcon={<Plus size={16} />} onClick={() => openFieldModal()}>
              Add field
            </Button>
          </Group>
        </AccordionItem>
        <AccordionItem label="Notification Emails">
          {notificationEmails.map((email, i) => (
            <Group key={i} mb="xs">
              <TextInput
                size={inputSize}
                value={email}
                onChange={(e) => updateEmail(i, e.target.value)}
                placeholder="Enter email address"
                style={{ flex: 1 }}
              />
              <TemplateReferenceMenu
                fields={fields}
                onInsert={(val) => insertPlaceholder("email", i, val)}
                targetType="email"
              />
              <Button
                size={inputSize}
                color="red"
                variant="outline"
                onClick={() => removeEmail(i)}
                style={isMobile ? { minHeight: 44, minWidth: 44 } : { minHeight: 36, minWidth: 36 }}
                aria-label="Remove email"
              >
                <Trash size={isMobile ? 18 : 16} />
              </Button>
            </Group>
          ))}
          <Group position="apart" mb="sm" mt={isMobile ? 14 : "xs"}>
            <Button size={inputSize} color="cap-blue" variant="outline" leftIcon={<Plus size={16} />} onClick={addEmail}>
              Add email
            </Button>
          </Group>
        </AccordionItem>
        <AccordionItem label="Notification WhatsApp Numbers">
          {notificationWhatsappNumbers.map((number, i) => (
            <Group key={`wa-${i}`} mb="xs">
              <TextInput
                size={inputSize}
                value={number}
                onChange={(e) => updateWhatsapp(i, e.target.value)}
                placeholder="Enter WhatsApp number (e.g. +27821234567)"
                style={{ flex: 1 }}
              />
              <TemplateReferenceMenu
                fields={fields}
                onInsert={(val) => insertPlaceholder("whatsapp", i, val)}
                targetType="whatsapp"
              />
              <Button
                size={inputSize}
                color="red"
                variant="outline"
                onClick={() => removeWhatsapp(i)}
                style={isMobile ? { minHeight: 44, minWidth: 44 } : { minHeight: 36, minWidth: 36 }}
                aria-label="Remove WhatsApp number"
              >
                <Trash size={isMobile ? 18 : 16} />
              </Button>
            </Group>
          ))}
          <Group position="apart" mb="sm" mt={isMobile ? 14 : "xs"}>
            <Button size={inputSize} color="cap-blue" variant="outline" leftIcon={<Plus size={16} />} onClick={addWhatsapp}>
              Add WhatsApp number
            </Button>
          </Group>
        </AccordionItem>
        <AccordionItem label="Notification Templates">
          <Accordion defaultValue="handlebars">
            <AccordionItem label="Content">
              <Group position="apart" mb="sm">
                <Button size={inputSize} color="cap-blue" variant="outline" leftIcon={<Robot size={16} />} onClick={generateTemplates} loading={generating}>
                  Generate with AI
                </Button>
                <Button size={inputSize} color="cap-blue" variant="outline" leftIcon={<TestPipe size={16} />} onClick={() => runTestTemplates(notificationSubjectLine, notificationBody, storyTemplate)} loading={testing}>
                  Test Templates
                </Button>
              </Group>
              <TextInput
                size={inputSize}
                label="Subject Line Template"
                value={notificationSubjectLine}
                onChange={(e) => setNotificationSubjectLine(e.target.value)}
                placeholder="Use Handlebars expressions like {{documentNumber}}"
                mb="sm"
                description="Define the notification email subject line."
              />
              <Textarea
                size={inputSize}
                label="Body Template"
                value={notificationBody}
                onChange={(e) => setNotificationBody(e.target.value)}
                placeholder="Use Handlebars expressions like {{documentNumber}}"
                minRows={3}
                mb="sm"
                description="Define the notification email body."
              />
              <Textarea
                size={inputSize}
                label="AI Story Template"
                placeholder="Use {{fieldId}} to inject values, e.g., A {{field1}} was submitted with {{field2}}."
                value={storyTemplate}
                onChange={(e) => setStoryTemplate(e.target.value)}
                mb="md"
                minRows={3}
                description="Define how form submissions are narrated as stories for AI context."
              />
            </AccordionItem>
            <AccordionItem label="Visuals">
              <Text weight={500} mb={8}>PDF image sizing</Text>
              <Grid columns={isMobile ? 1 : 3} gutter="sm" mb="md">
                <Grid.Col span={1}>
                  <NumberInput
                    size={inputSize}
                    label="Logo max width"
                    value={pdfLogoMaxWidth}
                    min={10}
                    max={400}
                    onChange={(value) => setPdfLogoMaxWidth(Number(value) || 0)}
                  />
                </Grid.Col>
                <Grid.Col span={1}>
                  <NumberInput
                    size={inputSize}
                    label="Photo max width"
                    value={pdfPhotoMaxWidth}
                    min={10}
                    max={800}
                    onChange={(value) => setPdfPhotoMaxWidth(Number(value) || 0)}
                  />
                </Grid.Col>
                <Grid.Col span={1}>
                  <NumberInput
                    size={inputSize}
                    label="Signature max width"
                    value={pdfSignatureMaxWidth}
                    min={10}
                    max={800}
                    onChange={(value) => setPdfSignatureMaxWidth(Number(value) || 0)}
                  />
                </Grid.Col>
              </Grid>
              <Switch
                size={inputSize}
                color="cap-blue"
                label="Hide empty values"
                checked={hideNullishInPdf}
                onChange={(e) => setHideNullishInPdf(e.currentTarget.checked)}
                mb="sm"
              />
            </AccordionItem>
          </Accordion>
        </AccordionItem>
        <AccordionItem label="Online Capture">
          <Switch
            size={inputSize}
            color="cap-blue"
            label="Enable Online Capture (Shareable Link)"
            checked={isPublicCaptureEnabled}
            onChange={(e) => setIsPublicCaptureEnabled(e.currentTarget.checked)}
            mb="xs"
          />
          <Text size="xs" color="dimmed" mb="sm" style={{ paddingLeft: 46 }}>
            Allows this form template to be completed online in a browser via a shareable link or QR code.
          </Text>
          {isPublicCaptureEnabled && (
            <>
              <Switch
                size={inputSize}
                color="cap-blue"
                label="Require Sign-in"
                checked={requireAuthentication}
                onChange={(e) => setRequireAuthentication(e.currentTarget.checked)}
                mb="xs"
              />
              <Text size="xs" color="dimmed" mb="sm" style={{ paddingLeft: 46 }}>
                If enabled, users must log in to Captiva Flex to submit the form. If disabled, guests can submit anonymously.
              </Text>
              {!requireAuthentication && (
                <TextInput
                  size={inputSize}
                  label="Default Guest Submitter Name"
                  placeholder="e.g. Online Guest"
                  value={guestSubmitterLabel}
                  onChange={(e) => setGuestSubmitterLabel(e.target.value)}
                  description="The name recorded as the submitter for guest submissions."
                  mb="sm"
                />
              )}
            </>
          )}
        </AccordionItem>
      </Accordion>
      </div>
      </ScrollArea>

      <Box
        sx={(theme) => ({
          borderTop: `1px solid ${theme.colorScheme === "dark" ? theme.colors.dark[4] : theme.colors.gray[2]}`,
          backgroundColor: theme.colorScheme === "dark" ? theme.colors.dark[7] : theme.white,
          paddingTop: theme.spacing.md,
          paddingLeft: isMobile ? theme.spacing.md : 0,
          paddingRight: isMobile ? theme.spacing.md : 0,
          paddingBottom: isMobile
            ? `calc(${theme.spacing.md}px + env(safe-area-inset-bottom, 0px))`
            : 0,
          display: "flex",
          justifyContent: "flex-end",
          gap: 12,
          flexShrink: 0,
        })}
      >
        <Button
          variant="default"
          color="gray"
          size={isMobile ? "md" : "sm"}
          style={{ minHeight: isMobile ? 44 : 36 }}
          leftIcon={<ArrowBackUp size={16} />}
          onClick={() => onCancel()}
        >
          Cancel
        </Button>
        <Button
          color="cap-navy"
          size={isMobile ? "md" : "sm"}
          style={{ minHeight: isMobile ? 44 : 36 }}
          leftIcon={<DeviceFloppy size={16} />}
          onClick={handleSave}
        >
          Save
        </Button>
      </Box>

      <Modal
        opened={modalOpen}
        size={isMobile ? "100%" : "lg"}
        onClose={() => setModalOpen(false)}
        title={editingField ? "Edit field" : "Add field"}
        {...({ fullScreen: isMobile } as any)}
        styles={{
          modal: {
            height: isMobile ? "100vh" : "auto",
            maxHeight: isMobile ? "100vh" : "85vh",
            width: isMobile ? "100vw" : undefined,
            margin: isMobile ? 0 : undefined,
            borderRadius: isMobile ? 0 : undefined,
            display: "flex",
            flexDirection: "column",
          },
          inner: isMobile ? { padding: 0 } : undefined,
          header: {
            flexShrink: 0,
            marginBottom: isMobile ? 0 : undefined,
            paddingBottom: isMobile ? 12 : undefined,
            borderBottom: isMobile ? "1px solid #e9ecef" : undefined,
          },
          body: {
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            padding: isMobile ? 16 : undefined,
          },
        }}
      >
        {editingField && (
          <FieldEditor
            field={editingField}
            onSave={saveField}
            onCancel={() => setModalOpen(false)}
            allTemplates={allTemplates}
            companyId={companyId}
            fields={fields}
            isPublicCaptureEnabled={isPublicCaptureEnabled}
          />
        )}
      </Modal>
      <Modal
        opened={suggestionsModalOpen}
        onClose={() => setSuggestionsModalOpen(false)}
        title="AI-Generated Templates"
      >
        {aiSuggestions && (
          <>
            <Text weight={500} mb="sm">Subject:</Text>
            <Text mb="md" style={{ background: "#f0f0f0", padding: "8px", borderRadius: "4px" }}>{aiSuggestions.subject}</Text>
            <Text weight={500} mb="sm">Body:</Text>
            <Text mb="md" style={{ background: "#f0f0f0", padding: "8px", borderRadius: "4px", whiteSpace: "pre-wrap" }}>{aiSuggestions.body}</Text>
            <Text weight={500} mb="sm">Story:</Text>
            <Text mb="md" style={{ background: "#f0f0f0", padding: "8px", borderRadius: "4px", whiteSpace: "pre-wrap" }}>{aiSuggestions.story}</Text>
            <Group position="right">
              <Button variant="default" color="gray" size={isMobile ? "md" : "sm"} style={{ minHeight: isMobile ? 44 : 36 }} onClick={() => setSuggestionsModalOpen(false)}>Reject</Button>
              <Button color="cap-navy" size={isMobile ? "md" : "sm"} style={{ minHeight: isMobile ? 44 : 36 }} onClick={acceptSuggestions}>Accept</Button>
            </Group>
          </>
        )}
      </Modal>
      <Modal
        opened={testModalOpen}
        onClose={() => setTestModalOpen(false)}
        title="Test Rendered Templates"
      >
        {testResult && (
          <>
            <Text weight={500} mb="sm">Rendered Subject:</Text>
            <Text mb="md" style={{ background: "#f0f0f0", padding: "8px", borderRadius: "4px" }}>{testResult.subject}</Text>
            <Text weight={500} mb="sm">Rendered Body:</Text>
            <Text mb="md" style={{ background: "#f0f0f0", padding: "8px", borderRadius: "4px", whiteSpace: "pre-wrap" }}>{testResult.body}</Text>
            {testResult.story && (
              <>
                <Text weight={500} mb="sm">Rendered Story:</Text>
                <Text mb="md" style={{ background: "#f0f0f0", padding: "8px", borderRadius: "4px", whiteSpace: "pre-wrap" }}>{testResult.story}</Text>
              </>
            )}
            <Text
              size="sm"
              color="blue"
              style={{ cursor: "pointer", textDecoration: "underline", marginTop: "1rem" }}
              onClick={() => setDataModalOpen(true)}
            >
              View raw test data
            </Text>
          </>
        )}
      </Modal>
      <Modal
        opened={dataModalOpen}
        onClose={() => setDataModalOpen(false)}
        title="Submission Data"
        size="xl"
      >
        {rawSubmission && flattenedData && (
          <>
            <Text weight={500} mb="sm">Raw Submission:</Text>
            <Textarea
              value={JSON.stringify(rawSubmission, null, 2)}
              readOnly
              minRows={10}
              mb="md"
              style={{ fontFamily: "'Roboto Mono', monospace" }}
            />
            <Text weight={500} mb="sm">Report Ingestion Data (flattened):</Text>
            <Textarea
              value={JSON.stringify(getAlaSqlFlattenedData(rawSubmission.data as Record<string, any>), null, 2)}
              readOnly
              minRows={10}
              mb="md"
              style={{ fontFamily: "'Roboto Mono', monospace" }}
            />
            <Text weight={500} mb="sm">Email & AI Data (flattened):</Text>
            <Textarea
              value={JSON.stringify(flattenedData, null, 2)}
              readOnly
              minRows={10}
              style={{ fontFamily: "'Roboto Mono', monospace" }}
            />
          </>
        )}
      </Modal>
    </div>
  );
}
